import type { VisibilityState } from '@tanstack/react-table';

import { useLocalStorage } from './useLocalStorage';

const STORAGE_PREFIX = 'column-visibility:';

/**
 * Column show/hide state for a data table, persisted to localStorage so the
 * user's choices survive page refreshes (and sync across tabs).
 *
 * @param tableId  Stable, unique id for the table (used as the storage key).
 * @param defaultVisibility  Applied only when nothing is stored yet — e.g. a
 *   table that hides some columns by default. Unknown column ids are ignored by
 *   react-table, so this stays safe as columns change.
 *
 * The returned setter accepts a value or an updater function, matching
 * react-table's `onColumnVisibilityChange` contract, so it can be passed straight through.
 */
export function useColumnVisibility(
  tableId: string,
  defaultVisibility: VisibilityState = {},
): [VisibilityState, (value: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void] {
  const [visibility, setVisibility] = useLocalStorage<VisibilityState>(
    `${STORAGE_PREFIX}${tableId}`,
    defaultVisibility,
  );
  return [visibility, setVisibility];
}
